import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Gate } from '../components/shell/gate.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { describeError } from '../lib/api.ts';
import { api, type DuplicateReport } from '../lib/client.ts';
import { useSession } from '../lib/session.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';

type Group = DuplicateReport['groups'][number];
type Person = Group['candidates'][number];

const show = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : typeof v === 'string' ? v : JSON.stringify(v);

/**
 * Duplicates (RELAY-947): the nightly report — candidates sharing an email or phone — with a
 * merge for each pair. Reached from the Candidates page.
 */
export function DuplicatesPage() {
  const session = useSession();
  const qc = useQueryClient();
  const report = useQuery({ queryKey: ['duplicates'], queryFn: () => api.candidates.duplicates() });
  const [merging, setMerging] = useState<{ group: Group } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  return (
    <Gate permission="candidate.read" feature="candidates.enabled">
      <PageHeader
        eyebrow={
          <a
            href="/candidates"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm transition-colors"
          >
            <ArrowLeft className="size-3.5" /> Candidates
          </a>
        }
        title="Possible duplicates"
        description={
          report.data?.generatedAt
            ? `From the nightly report of ${new Date(report.data.generatedAt).toLocaleString()}.`
            : 'The nightly report has not run yet.'
        }
        actions={
          notice && (
            <span className="text-muted-foreground text-sm" role="status">
              {notice}
            </span>
          )
        }
      />
      {report.isPending && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      )}
      {report.error != null && (
        <p className="text-danger text-sm" role="alert">
          {describeError(report.error)}
        </p>
      )}
      {report.data && report.data.groups.length === 0 && (
        <p className="text-muted-foreground text-sm" data-testid="no-duplicates">
          No duplicates found.
        </p>
      )}
      <div className="flex max-w-3xl flex-col gap-3">
        {report.data?.groups.map((g) => (
          <section
            key={`${g.field}:${g.value}`}
            className="border-border bg-card rounded-xl border p-3"
            data-testid="duplicate-group"
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-sm font-medium">
                Same {g.field}: <span className="font-mono">{g.value}</span>
              </h3>
              {session.can('candidate.update') && g.candidates.length >= 2 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setMerging({ group: g })}
                  data-testid="open-merge"
                >
                  Merge…
                </Button>
              )}
            </div>
            <ul className="text-sm">
              {g.candidates.map((c) => (
                <li key={c.id} className="flex gap-3">
                  <a className="text-brand hover:underline" href={`/candidates/${c.id}`}>
                    {c.name}
                  </a>
                  <span className="text-muted-foreground">
                    {c.email ?? ''} {c.phone ?? ''} · {c.stage ?? 'no stage'} · added{' '}
                    {new Date(c.createdAt).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      {merging && (
        <MergeDialog
          people={merging.group.candidates}
          onClose={() => setMerging(null)}
          onMerged={async (keptName) => {
            setMerging(null);
            setNotice(`Merged into ${keptName}.`);
            await qc.invalidateQueries({ queryKey: ['duplicates'] });
            await qc.invalidateQueries({ queryKey: ['records'] });
          }}
        />
      )}
    </Gate>
  );
}

/**
 * Merge two candidates: pick which record to keep, then for every field that differs, which
 * side's value to keep. Suggested choices fill gaps (the kept record's empty fields take the
 * other's value). Notes, tasks, documents and history always move to the kept record.
 */
function MergeDialog({
  people,
  onClose,
  onMerged,
}: {
  people: Person[];
  onClose: () => void;
  onMerged: (keptName: string) => void | Promise<void>;
}) {
  const [keepId, setKeepId] = useState(people[0]!.id);
  const otherId = people.find((p) => p.id !== keepId)!.id;
  const preview = useQuery({
    queryKey: ['merge-preview', keepId, otherId],
    queryFn: () => api.candidates.mergePreview(keepId, otherId),
  });
  const [take, setTake] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const chosen = (field: string, suggested: 'survivor' | 'other') =>
    take[field] ?? suggested === 'other';
  const merge = useMutation({
    mutationFn: () =>
      api.candidates.merge(
        keepId,
        otherId,
        (preview.data?.fields ?? [])
          .filter((f) => !f.same && chosen(f.field, f.suggested))
          .map((f) => f.field),
      ),
    onSuccess: () => onMerged(people.find((p) => p.id === keepId)!.name),
    onError: (err) => setError(describeError(err)),
  });
  const differing = (preview.data?.fields ?? []).filter((f) => !f.same);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        data-testid="merge-dialog"
      >
        <DialogHeader>
          <DialogTitle>Merge candidates</DialogTitle>
          <DialogDescription>
            Choose the record to keep and, field by field, which value it keeps.
          </DialogDescription>
        </DialogHeader>
        <fieldset className="flex flex-col gap-1 text-sm">
          <legend className="mb-1 font-medium">Keep</legend>
          {people.slice(0, 2).map((p) => (
            <label key={p.id} className="flex items-center gap-2">
              <input
                type="radio"
                name="keep"
                checked={keepId === p.id}
                onChange={() => {
                  setKeepId(p.id);
                  setTake({});
                }}
                aria-label={`Keep ${p.name}`}
              />
              {p.name}{' '}
              <span className="text-muted-foreground">
                (added {new Date(p.createdAt).toLocaleDateString()})
              </span>
            </label>
          ))}
        </fieldset>
        {preview.isPending && <p className="text-muted-foreground text-sm">Comparing…</p>}
        {preview.data && (
          <>
            <table className="w-full text-sm" data-testid="merge-fields">
              <thead className="text-muted-foreground text-left text-xs uppercase">
                <tr>
                  <th className="py-1">Field</th>
                  <th className="py-1">Kept record</th>
                  <th className="py-1">Other record</th>
                </tr>
              </thead>
              <tbody>
                {differing.map((f) => (
                  <tr
                    key={f.field}
                    className="border-border border-t"
                    data-testid={`merge-field-${f.field}`}
                  >
                    <td className="py-1 pr-2 font-medium">{f.field.replace(/^custom\./, '')}</td>
                    <td className="py-1 pr-2">
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name={f.field}
                          checked={!chosen(f.field, f.suggested)}
                          onChange={() => setTake({ ...take, [f.field]: false })}
                          aria-label={`Keep ${f.field} from the kept record`}
                        />
                        {show(f.survivor)}
                      </label>
                    </td>
                    <td className="py-1">
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name={f.field}
                          checked={chosen(f.field, f.suggested)}
                          onChange={() => setTake({ ...take, [f.field]: true })}
                          aria-label={`Take ${f.field} from the other record`}
                        />
                        {show(f.other)}
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {differing.length === 0 && (
              <p className="text-muted-foreground text-sm">Every field already matches.</p>
            )}
            <p className="text-muted-foreground text-xs">
              Moves to the kept record: {preview.data.moves.notes} notes, {preview.data.moves.tasks}{' '}
              tasks, {preview.data.moves.documents} documents, and the other record&apos;s history.
              The other record is then removed (kept for auditing).
            </p>
          </>
        )}
        {(error || preview.error != null) && (
          <p className="text-danger text-sm" role="alert">
            {error ?? describeError(preview.error)}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => merge.mutate()}
            disabled={!preview.data || merge.isPending}
            data-testid="confirm-merge"
          >
            {merge.isPending && <Loader2 className="animate-spin" />} Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
